import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import "../../styles/serviceCallStyles/ServiceCallDates.css";

const ServiceCallDates = forwardRef(({ data, setData }, ref) => {
  const [dateRanges, setDateRanges] = useState(() => {
    const storedDateRanges =
      JSON.parse(localStorage.getItem("dateRanges")) || {};
    const parsedDateRanges = {};

    Object.entries(storedDateRanges).forEach(([taskId, teamData]) => {
      parsedDateRanges[taskId] = {};
      Object.entries(teamData).forEach(
        ([teamIndex, { startDate, endDate, startTime, endTime }]) => {
          parsedDateRanges[taskId][teamIndex] = {
            startDate: startDate ? dayjs(startDate) : null,
            endDate: endDate ? dayjs(endDate) : null,
            startTime: startTime || null,
            endTime: endTime || null,
          };
        }
      );
    });

    return parsedDateRanges;
  });

  const [validationError, setValidationError] = useState("");

  const combineDateAndTime = (date, time) => {
    const formattedDate = dayjs(date).format("YYYY-MM-DD");
    const combinedDateTime = dayjs(
      `${formattedDate} ${time}`,
      "YYYY-MM-DD h:mm A"
    );
    return combinedDateTime.isValid()
      ? combinedDateTime.format("YYYY-MM-DD HH:mm:ss")
      : null;
  };

  const validateDateRanges = () => {
    let isValid = true;

    Object.entries(dateRanges).forEach(([taskId, teamData]) => {
      Object.values(teamData).forEach(
        ({ startDate, endDate, startTime, endTime }) => {
          if (!startDate || !endDate || !startTime || !endTime) {
            isValid = false;
          }
        }
      );
    });

    setValidationError(
      isValid
        ? ""
        : "All employees must be assigned valid start dates, end dates, and times."
    );

    localStorage.setItem("serviceCallDatesErrors", JSON.stringify({ isValid }));
    return isValid;
  };

  // Expose validation logic through ref
  useImperativeHandle(ref, () => ({
    validateForm: () => {
      return validateDateRanges();
    },
  }));

  useEffect(() => {
    localStorage.setItem("dateRanges", JSON.stringify(dateRanges));
  }, [dateRanges]);

  const handleDateChange = (dates, taskId, teamIndex) => {
    const [startDate, endDate] = dates;
    const updatedDateRanges = {
      ...dateRanges,
      [taskId]: {
        ...(dateRanges[taskId] || {}),
        [teamIndex]: {
          ...dateRanges[taskId]?.[teamIndex],
          startDate,
          endDate,
        },
      },
    };
    setDateRanges(updatedDateRanges);
    setData({ ...data, dateRanges: updatedDateRanges });
  };

  const handleTimeChange = (time, taskId, teamIndex, timeType) => {
    const updatedDateRanges = {
      ...dateRanges,
      [taskId]: {
        ...(dateRanges[taskId] || {}),
        [teamIndex]: {
          ...dateRanges[taskId]?.[teamIndex],
          [timeType]: time ? time.format("h:mm A") : null,
        },
      },
    };
    setDateRanges(updatedDateRanges);
    setData({ ...data, dateRanges: updatedDateRanges });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <div className="servicecall-dates-container">
        <div className="servicecalldatesScreen">
          <div className="servicecalldates-header-container">
            <h3 className="service-title">Service Call Dates</h3>
            <table className="service-table">
              <thead>
                <tr>
                  <td className="service-item">Task</td>
                  <td className="service-item">Team</td>
                  <td className="service-item">Dates</td>
                  <td className="service-item">Start Time</td>
                </tr>
              </thead>
            </table>

            <div className="servicecalldates-form-container">
              <table className="servicedates-table">
                <tbody>
                  {data.selectedTasks?.map((task) => (
                    <React.Fragment key={task.taskid}>
                      <tr className="task-category">
                        <td colSpan="4">
                          <strong>{task.taskname}</strong>
                        </td>
                      </tr>
                      <tr>
                        <td className="task-list">
                          <ul>
                            {task.subtasklist.map((subTask) => (
                              <li key={subTask.value}>{subTask.label}</li>
                            ))}
                          </ul>
                        </td>
                        <td className="team-info">
                          {task.employeelist.map((teamMember, teamIndex) => (
                            <div className="team-row" key={teamIndex}>
                              <span className="employeeNameSpan">
                                {teamMember.label}
                              </span>
                              <span className="date-wrapper">
                                <DatePicker
                                  label="Start Date"
                                  value={
                                    dateRanges[task.taskid]?.[teamIndex]
                                      ?.startDate || null
                                  }
                                  onChange={(value) =>
                                    handleDateChange(
                                      [
                                        value,
                                        dateRanges[task.taskid]?.[teamIndex]
                                          ?.endDate,
                                      ],
                                      task.taskid,
                                      teamIndex
                                    )
                                  }
                                />
                                <span>to</span>
                                <DatePicker
                                  label="End Date"
                                  value={
                                    dateRanges[task.taskid]?.[teamIndex]
                                      ?.endDate || null
                                  }
                                  onChange={(value) =>
                                    handleDateChange(
                                      [
                                        dateRanges[task.taskid]?.[teamIndex]
                                          ?.startDate,
                                        value,
                                      ],
                                      task.taskid,
                                      teamIndex
                                    )
                                  }
                                />
                              </span>
                              <span className="time-wrapper">
                                <TimePicker
                                  label="Start Time"
                                  value={
                                    dateRanges[task.taskid]?.[teamIndex]
                                      ?.startTime
                                      ? dayjs(
                                          dateRanges[task.taskid][teamIndex]
                                            .startTime,
                                          "h:mm A"
                                        )
                                      : null
                                  }
                                  onChange={(value) =>
                                    handleTimeChange(
                                      value,
                                      task.taskid,
                                      teamIndex,
                                      "startTime"
                                    )
                                  }
                                  ampm
                                />
                                <span>to</span>
                                <TimePicker
                                  label="End Time"
                                  value={
                                    dateRanges[task.taskid]?.[teamIndex]
                                      ?.endTime
                                      ? dayjs(
                                          dateRanges[task.taskid][teamIndex]
                                            .endTime,
                                          "h:mm A"
                                        )
                                      : null
                                  }
                                  onChange={(value) =>
                                    handleTimeChange(
                                      value,
                                      task.taskid,
                                      teamIndex,
                                      "endTime"
                                    )
                                  }
                                  ampm
                                />
                              </span>
                            </div>
                          ))}
                        </td>
                      </tr>
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        {validationError && (
          <div className="validation-error">
            <span className="error-text">{validationError}</span>
          </div>
        )}
      </div>
    </LocalizationProvider>
  );
});

export default ServiceCallDates;
